import type { z } from 'zod';

import {
  ApiError,
  errorResponseSchema,
  NETWORK_ERROR_CODE,
  NETWORK_ERROR_MESSAGE,
  UNEXPECTED_RESPONSE_CODE,
  UNEXPECTED_RESPONSE_MESSAGE,
} from './errors';

/**
 * The CSRF-aware boundary every owner request goes through. Mirrors
 * `backend/app/api/security.py`:
 *
 * - the session travels as an `HttpOnly` cookie the browser attaches on its own
 *   (`credentials: 'same-origin'`, never manual);
 * - every non-safe method echoes the readable `nv_csrf` cookie back as `X-CSRF-Token`,
 *   because holding the cookie is what proves the request came from this app, not the
 *   session cookie a cross-site request could not read anyway.
 */

const CSRF_COOKIE_NAME = 'nv_csrf';
/** Exported so a non-`fetch` request (the `XMLHttpRequest` upload in `capture/api.ts`, used
 * for real upload-body progress) can echo the same header this boundary uses. */
export const CSRF_HEADER_NAME = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Matches `app.correlation.CORRELATION_ID_HEADER` - every response carries one, minted by
 * `CorrelationIdMiddleware` even when the request itself sent none. */
const CORRELATION_ID_HEADER = 'x-correlation-id';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  readonly method?: HttpMethod;
  /** A plain JSON-serializable body, or a `FormData` body (sent as-is, so the browser sets its
   * own `multipart/form-data` boundary) for `POST /notes`. */
  readonly body?: unknown;
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

/** The readable CSRF cookie, or `null` before any session exists. Shared by this boundary and
 * by `capture/api.ts`'s `XMLHttpRequest` upload, so there is exactly one place that reads it. */
export function getCsrfToken(): string | null {
  return readCookie(CSRF_COOKIE_NAME);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};
  const init: RequestInit = { method, headers, credentials: 'same-origin' };

  if (options.body !== undefined) {
    if (options.body instanceof FormData) {
      // No Content-Type here: the browser must set it itself, with the multipart boundary.
      init.body = options.body;
    } else {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }
  }

  if (!SAFE_METHODS.has(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken !== null) {
      headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }

  try {
    return await fetch(path, init);
  } catch {
    // A network failure, a blocked request, or the dev proxy target being down: none of
    // these carry a parseable body, so this is the one error message not sourced from the
    // server. It is deliberately generic - CLAUDE.md keeps technical detail out of owner-
    // facing text.
    throw new ApiError(0, NETWORK_ERROR_CODE, NETWORK_ERROR_MESSAGE);
  }
}

async function readJsonBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

async function throwForFailedResponse(response: Response): Promise<never> {
  const correlationId = response.headers.get(CORRELATION_ID_HEADER) ?? undefined;
  const body = await readJsonBody(response);
  const parsed = errorResponseSchema.safeParse(body);
  if (parsed.success) {
    throw new ApiError(response.status, parsed.data.code, parsed.data.message, correlationId);
  }
  throw new ApiError(
    response.status,
    UNEXPECTED_RESPONSE_CODE,
    UNEXPECTED_RESPONSE_MESSAGE,
    correlationId,
  );
}

/** `GET`/`POST`/... that expects and validates a JSON body, such as `GET /me`. */
export async function requestJson<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const response = await send(path, options);
  if (!response.ok) {
    await throwForFailedResponse(response);
  }

  const body = await readJsonBody(response);
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new ApiError(response.status, UNEXPECTED_RESPONSE_CODE, UNEXPECTED_RESPONSE_MESSAGE);
  }
  return parsed.data;
}

/** A request whose success carries no body, such as `POST /auth/logout` (`204`). */
export async function requestVoid(path: string, options: RequestOptions = {}): Promise<void> {
  const response = await send(path, options);
  if (!response.ok) {
    await throwForFailedResponse(response);
  }
}
